<template>
  <div class="styled-basket">
    <BaseBasket
      #default="{
                  orderLines,
                  voucherCodes,
                  totals,
                  refreshData,
                  expectingNewLine,
                  initialLoading,
                  messages,
                  clearMessages,
                }"
      @success="refreshData()">
      <template v-if="!initialLoading">
        <b-alert :show="messages && messages.length > 0"
                 dismissible
                 @dismissed="clearMessages">
          <h2 class="alert-heading">
            Note:
          </h2>
          <ul class="alert-list">
            <li v-for="(message, index) in messages"
                :key="index"
                class="alert-item">
              {{ message }}
            </li>
          </ul>
        </b-alert>
        <template v-if="orderLines && orderLines.length > 0">
          <SimpleBasketOrderLinesList>
            <SimpleBasketOrderLine
              v-for="(orderLine, index) in orderLines"
              :key="orderLine.id"
              v-model="orderLines[index]"
              :lock-quantity="isQtyLocked(orderLine)"
              :input-class="inputClass"
              :enable-update-spinner="false"
              btn-class="btn btn-danger"
              @update="refreshData()">
              <template #default>
                <div>
                  <component
                    :is="orderLine.hasWebPage ? 'a' : 'span'"
                    :href="`/products/${orderLine.productId}`"
                    target="_blank"
                    class="product-display-name">
                    {{ orderLine.productDisplayName }}
                  </component>

                  <span v-if="orderLine.hasWebPage"
                        class="product-display-icon">
                    <font-awesome-icon icon="external-link"></font-awesome-icon>
                  </span>
                </div>

                <div class="styled-basket__pricing">
                  {{ getPricingDisplayString(orderLine) }}
                </div>

                <template
                  v-if="
                    getBookingValidity(orderLine.id) === null ||
                      getBookingValidity(orderLine.id).notPastLastBookingDate === true
                  ">
                  <span
                    v-if="hasInvalidBooking(orderLine.id) || requiresAddress(orderLine.id)"
                    class="booking-btn-invalid">
                    <font-awesome-icon
                      icon="exclamation-circle"
                      transform="down-1"></font-awesome-icon>
                  </span>
                  <button
                    v-if="showAddressButton(orderLine.productId)"
                    :class="[
                      'btn btn-link edit-booking-btn',
                      { invalid: requiresAddress(orderLine.id) }
                    ]"
                    aria-label="Edit address"
                    @click="onEditAddress(orderLine)">
                    Edit address
                  </button>

                  <button
                    v-if="getBooking(orderLine.id)"
                    :class="[
                      'btn btn-link edit-booking-btn',
                      { invalid: hasInvalidBooking(orderLine.id) }
                    ]"
                    aria-label="Edit booking"
                    @click="onEditBooking(orderLine)">
                    Edit booking
                  </button>
                  <!---->
                  <move-order-line-to-wish-list
                    :logged-in="loggedIn"
                    :order-line="orderLine"
                    @loading="
                      isLoading => {
                        loading = isLoading;
                      }
                    "
                    @update="refreshData()">
                  </move-order-line-to-wish-list>
                </template>
                <template v-else>
                  <span class="order-line-notification__error">
                    <font-awesome-icon icon="exclamation-circle"
                                       transform="down-1">
                    </font-awesome-icon>
                    This event is no longer available, remove it from your basket to continue
                  </span>
                </template>
              </template>
              <template #delete-label>
                <font-awesome-icon :icon="['far', 'trash-alt']"></font-awesome-icon>
              </template>
            </SimpleBasketOrderLine>

            <tr v-if="expectingNewLine"
                class="simple-basket__loading">
              <td colspan="100%">
                <loading-spinner></loading-spinner>
              </td>
            </tr>
          </SimpleBasketOrderLinesList>

          <div class="simple-basket__summary">
            <div class="simple-basket__pricing">
              <div v-if="voucherError"
                   class="alert alert-danger simple-basket__error">
                {{ voucherError }}
              </div>

              <OrderTotals :value="totals"></OrderTotals>

              <div v-if="enableVouchers"
                   class="simple-basket__vouchers">
                <ApplyVoucher
                  :applied-voucher-codes="voucherCodes"
                  :input-class="inputClass"
                  :btn-class="btnClass"
                  @update="refreshData()"
                  @response="onApplyVoucherResponse"></ApplyVoucher>
                Please note that only one voucher code may be used per transaction
              </div>
            </div>

            <div v-if="enableClear"
                 class="simple-basket__clear">
              <DeleteAllOrderLines
                class="btn btn-danger"
                @update="refreshData()"></DeleteAllOrderLines>
            </div>

            <slot name="submit"></slot>
          </div>

          <EventBookingModal
            :show="showBookingModal"
            :event-title="eventTitle"
            :instance="selectedInstance"
            :web-account-id="webAccountId"
            :value="selectedBookingId"
            :pricing-schedule-id="selectedPricingSchedule"
            :readonly="validBookings"
            :relationship-type-ids="relationshipTypeIds"
            edit
            @input="refreshBookings()"
            @close="onCloseBookingModal"></EventBookingModal>

          <DeliveryAddressModal
            :show="showDeliveryAddressModal"
            :search-type-id="searchTypeId"
            :order-lines="selectedOrderLines"
            no-skip
            @refresh-addresses="refreshAddresses()"
            @close="onCloseDeliveryAddressModal"></DeliveryAddressModal>
        </template>

        <template v-else>
          <div class="simple-basket__empty">
            <slot name="empty">
              Your basket is currently empty.
            </slot>
          </div>
        </template>
      </template>
      <template v-else>
        <loading-spinner></loading-spinner>
      </template>
    </BaseBasket>
    <div v-show="(refresh || loading) && !firstLoad"
         class="styled-basket__loading">
      <slot name="loading">
        <loading-spinner></loading-spinner>
      </slot>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Ecommerce from "spiritweb.packages.ecommerce";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import { listBookings, getInstance } from "~/Scripts/resources/eventsResource";
import { getBasketProducts } from "~/Scripts/resources/basketStepResource";
import EventBookingModal from "~/Components/Events/EventBookingModal.vue";
import DeliveryAddressModal from "~/Components/Product/DeliveryAddressModal.vue";
import MoveOrderLineToWishList from "~/Components/MoveOrderLineToWishList.vue";

Vue.use( Ecommerce );

/**
 * @typedef {Object} BookingValidationModel
 * @property {string} [orderLineId] UUID The order line associated with the booking
 * @property {string} [bookingId] UUID The ID of the booking being validated
 * @property {boolean} valid Is the booking valid?
 * @property {boolean} bookingHasNotBeenAbandoned Determine if the booking has been abandoned
 * @property {boolean} notPastLastBookingDate Determine if the booking is
 * passed it's LastBookingDate
 * @property {boolean} bookingHasNotExpired Ensures the booking expiry date/time hasn't passed
 * @property {boolean} allDelegateDetailsProvided Determine if all delegate details have been
 * provided (name, email, etc)
 * @property {boolean} allReservedDelegatesProvided Determine if the number of reserved delegates
 * matches the number of delegates we have saved
 * @property {boolean} matchesOrderLineQuantity Determine if the order line has the correct
 * quantity set
 */

/**
 * @typedef {Object} BookingListValidationModel A model for determining the validity of a list of
 * event bookings
 * @property {boolean} valid whether all of the contained bookings are valid
 * @property {Array<BookingValidationModel>} bookings The validation results of each booking
 */

export default {
  name: "StyledBasket",

  components: {
    LoadingSpinner,
    EventBookingModal,
    DeliveryAddressModal,
    MoveOrderLineToWishList
  },

  props: {
    enableVouchers: {
      type: Boolean,
      default: false
    },
    enableClear: {
      type: Boolean,
      default: false
    },
    inputClass: {
      type: String,
      default: "form-control"
    },
    btnClass: {
      type: String,
      default: "btn btn-accent-8"
    },
    checkoutUrl: {
      type: String,
      default: null
    },
    webAccountId: {
      type: String,
      default: null
    },
    /** A model containing validation data
     * @type {import('vue').PropType<BookingValidationModel>}
     */
    bookingValidations: {
      type: Object,
      default: () => null
    },
    addressValidations: {
      type: Object,
      default: () => null
    },
    searchTypeId: {
      type: String,
      default: null
    },
    refresh: {
      type: Boolean,
      default: false
    },
    loggedIn: {
      type: Boolean,
      default: false
    },
    delegateBookingViewModel: {
      type: Object,
      default: () => ( {} )
    },
    validBookings: {
      type: Boolean,
      default: true
    },
    relationshipTypeIds: {
      type: Array,
      default: () => []
    }
  },
  provide () {
    return {
      delegateBookingViewModel: this.delegateBookingViewModel
    };
  },
  data: () => ( {
    bookings: [],
    showBookingModal: false,
    selectedOrderLine: {},
    selectedInstance: null,
    selectedPricingSchedule: null,
    showDeliveryAddressModal: null,
    products: [],
    internalLoggedIn: false,
    voucherError: null,
    loading: false,
    firstLoad: true
  } ),

  computed: {
    selectedOrderLines () {
      let result = null;

      if ( this.selectedOrderLine?.id ) {
        result = [this.selectedOrderLine.id];
      }

      return result;
    },
    selectedBookingId () {
      let result = null;

      if ( this.selectedBooking ) {
        result = this.selectedBooking.eventBookingId;
      }

      return result;
    },
    selectedBooking () {
      let result = null;

      if ( this.bookings && this.selectedOrderLine ) {
        result = this.getBooking( this.selectedOrderLine.id );
      }

      return result;
    },
    eventTitle () {
      let result = "Untitled event";

      if ( this.selectedOrderLine ) {
        result = this.selectedOrderLine.productDisplayName;
      }

      return result;
    }
  },
  watch: {
    refresh ( val ) {
      if ( val ) {
        this.refreshData();
      }
    }
  },

  created () {
    // Set up event handlers that are fired at the very start and very end of
    // updating the basket quantity

    // These events trigger the standard "Basket Update"
    this.$root.$on( "sw::setBasketItemQuantity::stop", this.onStartUpdate );
    this.$root.$on( "sw::basket::request", this.onStartUpdate );
    this.$root.$on( "sw::removeFromBasket::request", this.onStartUpdate );
  },

  methods: {
    /**
     * Turn on the basket update spinner
     */
    onStartUpdate () {
      this.loading = true;
    },

    /**
     * Turn off the basket update spinner
     */
    onEndUpdate () {
      this.loading = false;
    },

    refreshData () {
      const refreshBookings = this.refreshBookings();

      const getProducts = getBasketProducts().then( response => {
        this.products = response.data;
      } );

      return Promise.all( [refreshBookings, getProducts] ).finally( () => {
        this.$emit( "refreshed" );

        // Basket has completely refreshed (base basket and local styled basket)
        // so we can turn off the loading spinner
        this.loading = false;

        // Set a flag to say the basket is no longer initialising for the first time
        this.firstLoad = false;
      } );
    },
    refreshAddresses () {
      this.$emit( "refresh-addresses" );
    },
    refreshBookings () {
      this.$emit( "refresh-bookings" );

      return listBookings().then( response => {
        if ( response.data ) {
          this.bookings = response.data;
        }
      } );
    },
    getBooking ( orderLineId ) {
      let result = null;

      if ( this.bookings?.length > 0 && orderLineId ) {
        const matches = this.bookings.filter( x => x.orderLineId === orderLineId );

        matches.sort( ( a, b ) => new Date( b.bookingDateTime ) - new Date( a.bookingDateTime ) );

        if ( matches?.length ) {
          [result] = matches;
        }
      }

      return result;
    },
    /**
     * @param {string} orderLineId UUID
     * @returns {BookingValidationModel} */
    getBookingValidity ( orderLineId ) {
      let result = null;

      /** @type {BookingListValidationModel} */
      const validations = this.bookingValidations;

      if ( validations?.bookings ) {
        const match = validations.bookings.find( x => x.orderLineId === orderLineId );

        if ( match ) {
          result = match;
        }
      }

      return result;
    },
    getAddressValidity ( orderLineId ) {
      let result = null;

      if ( this.addressValidations?.orderLines ) {
        const match = this.addressValidations.orderLines.find( x => x.orderLineId === orderLineId );

        if ( match ) {
          result = match;
        }
      }

      return result;
    },
    hasInvalidBooking ( orderLineId ) {
      let result = false;

      if ( this.bookingValidations ) {
        const validity = this.getBookingValidity( orderLineId );

        if ( validity && !validity.valid ) {
          result = true;
        }
      }

      return result;
    },
    requiresAddress ( orderLineId ) {
      let result = false;

      if ( this.addressValidations ) {
        const validity = this.getAddressValidity( orderLineId );

        if ( validity && !validity.valid ) {
          result = true;
        }
      }

      return result;
    },
    getPricingDisplayString ( orderLine ) {
      let result = "";

      if ( orderLine ) {
        result = orderLine.pricingScheduleDescription;
      }

      if ( orderLine.productLicenceTypeId > 0 || orderLine.productFormatId > 0 ) {
        let suffix = "";

        if ( orderLine.productFormatId > 0 ) {
          suffix += orderLine.productFormat;
        }

        if ( orderLine.productLicenceTypeId > 0 ) {
          if ( suffix.length ) {
            suffix += ", ";
          }

          suffix += orderLine.productLicenceType;
        }

        result += ` - (${suffix})`;
      }

      return result;
    },
    onEditAddress ( orderLine ) {
      this.selectedOrderLine = orderLine;
      this.showDeliveryAddressModal = true;
    },
    onEditBooking ( orderLine ) {
      this.selectedOrderLine = orderLine;
      this.selectedPricingSchedule = orderLine?.pricingScheduleId;

      if ( this.selectedBooking ) {
        getInstance( this.selectedBooking.eventInstanceId, orderLine.pricingScheduleId )
          .then( response => {
            this.selectedInstance = response.data;
            this.showBookingModal = true;
          } )
          .catch( e => {
            this.showBookingModal = false;
            throw e;
          } );
      } else {
        throw new Error( "Booking not found" );
      }
    },
    onApplyVoucherResponse ( response ) {
      this.voucherError = null;

      if ( response ) {
        if ( !response.valid ) {
          this.voucherError = response.userMessage;
        }
      }
    },
    getMatchingProduct ( productId ) {
      let result = null;

      if ( this.products ) {
        const match = this.products.find( x => x.productId === productId );

        if ( match ) {
          result = match;
        }
      }

      return result;
    },
    showAddressButton ( productId ) {
      let result = false;

      const product = this.getMatchingProduct( productId );
      if ( product ) {
        result = product.RequiresDeliveryAddressOnSopLine;
      }

      return result;
    },
    onCloseBookingModal () {
      this.selectedOrderLine = null;
      this.selectedInstance = null;
      this.showBookingModal = false;
    },
    onCloseDeliveryAddressModal () {
      this.selectedOrderLine = null;
      this.showDeliveryAddressModal = false;
    },
    qtyLockedByPrice ( orderLine ) {
      const {
        productId,
        pricingScheduleId,
        productLicenceTypeId,
        productFormatId
      } = orderLine;

      const matchedProduct = this.getMatchingProduct( productId );
      if ( !matchedProduct?.productPrices?.length ) {
        return false;
      }

      // Find the correct price based on the unique schedule, format and licence type combination
      const matchedPrice = matchedProduct.productPrices
        .find( x => x.pricingScheduleId === pricingScheduleId
        && x.productLicenceTypeId === productLicenceTypeId
        && x.productFormatId === productFormatId );

      return matchedPrice?.metaData?.website_DefaultQuantityLocked === "1";
    },
    isQtyLocked ( orderLine ) {
      return !!this.getBooking( orderLine.id )
        || this.qtyLockedByPrice( orderLine );
    }
  }
};
</script>

<style lang="less" scoped>
@import "../Styles/utils/colors.less";

.styled-basket {
  position: relative;

  &__loading {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.7);
    margin: -20px -30px -80px -30px;
  }

  &__pricing {
    color: #777;
    font-size: 14px;
  }

  .simple-basket__vouchers .apply-voucher {
    margin-bottom: 10px;
  }

  /deep/ th {
    font-size: 16px;
    font-weight: 700;
  }

  /deep/ tr {
    border-bottom: 2px solid #b1d6d4;
  }

  /deep/ tbody tr:last-child {
    border-bottom: 0;
  }

  /deep/ .order-line__name {
    font-weight: 600;
  }

  /deep/ .order-line-quantity .form-control {
    padding: 0.375rem 0.75rem;
  }

  /deep/ .order-line__price {
    border-radius: 10px;
    padding: 5px;
    text-align: center;
    font-weight: 600;
  }

  /deep/ .delete-order-line {
    padding: 5px 10px;
  }

  /deep/.simple-basket__error {
    margin-top: 20px;
  }

  /deep/ .simple-basket__summary {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  /deep/ .order-totals,
  /deep/ .simple-basket__pricing {
    margin-bottom: 0 !important;
    display: flex;
    flex-direction: column;
  }

  /deep/ .order-totals__content {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  /deep/ .order-totals__table {
    font-weight: 600;
    margin-bottom: 10px;
    width: 100%;
    th {
      font-size: 16px;
      padding: 0 20px 0 0 !important;
    }

    td {
      padding: 0 !important;
      text-align: right;
    }

    tr {
      border-bottom: 0;
    }
  }

  /deep/ .simple-basket__empty {
    text-align: center;
    color: @color-accent-1;
    display: block;
    font-weight: 600;
    padding: 30px 15px;
  }

  /deep/ .apply-voucher {
    margin-top: 5px;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
  }

  /deep/ .apply-voucher__current-voucher {
    margin-bottom: 5px;
  }

  /deep/ .apply-voucher__input-container {
    display: flex;
  }

  /deep/ .apply-voucher__button {
    font-size: 14px;
    font-weight: 500;
  }

  .product-display-name {
    color: #000;
  }

  .product-display-icon {
    font-size: 10px;
    color: #555;
    display: inline-block;
    margin-left: 3px;
  }

  .edit-booking-btn {
    padding: 0;
    border: 0;

    &.invalid {
      color: red;
    }
  }

  .order-line-notification__error {
    color: red;
  }

  .booking-btn-invalid {
    color: red;
    display: inline-block;
    margin-left: 5px;
  }
}
</style>
